"use client";

import { useEffect, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";
export default function DirectAnswerBlock() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const progress = (viewportH - rect.top) / (viewportH + rect.height);
      setOffset(progress * 100);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const paragraphs = ["We exist for the mark. For the precise moment when laser, metal, and intent converge inside a controlled environment and industrial standards meet absolute clarity. Our facility is not just a workshop. It is a standard — a refusal of the imprecise label and a quiet insistence that industrial identification, in its purest form, remains an act of engineering excellence.", "Our founding question was simple. What happens to critical infrastructure when it is stripped of ambiguity, of error, of the relentless risk of misidentification? We believe the answer is compliance — not only of the tags we manufacture, but of the systems they protect.", "Every component produced within these walls has been crafted with intent. We seek out the complex, the high-stakes, the technical specifications that industry demands. We calibrate them micron by micron, layer by layer, until the original design of the engineer functions perfectly once more.", "We do not chase trends. We chase precision. The durability of stainless, the contrast of phenolic, the permanence of deep-etching — these are not mere options. They are essential requirements, and we offer the facility in which to guarantee them.", "Our clients are not just buyers. They are partners in a rigorous process. They arrive with requirements, they leave with solutions, and between those two states we offer everything we have — laser technology, material science, precision tooling, and the company of those who still believe a label deserves a lifetime of service.", "We are deliberately focused. We will remain so. Efficiency is not strategy here; it is the precondition of quality. There is no guesswork at our facility. There is only the engineer, the technician, and the material.", "To partner is to accept the terms of the specification. To rely on us is to remember why industrial marking was ever called essential in the first place."];
  const firstWord = paragraphs[0].slice(1);
  return <section data-section-id="2828" id="direct-answer-block" ref={sectionRef} className="relative overflow-hidden bg-background text-foreground py-[200px]">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-end justify-end overflow-hidden select-none" style={{
      transform: `translate3d(0, ${offset * 0.3 - 15}px, 0)`
    }}>
        <span className="font-default font-bold leading-none translate-x-[8%] translate-y-[15%]" style={{
        fontSize: "30vw",
        color: "var(--card)",
        letterSpacing: "-0.06em"
      }}>
          MARK
        </span>
      </div>

      {}
      <div className="relative z-10 px-6 md:px-12">
        <div className="max-w-[720px] mx-auto md:ml-[max(1.5rem,calc(50%-540px))] md:mr-auto">
          {}
          <p className="font-mono text-xs md:text-sm uppercase mb-12 md:mb-16" style={{
          color: "var(--primary)",
          letterSpacing: "0.15em"
        }}>
            ABOUT — 01 · OUR MISSION
          </p>

          {}
          <h2 className="font-default font-bold leading-[0.95] mb-20" style={{
          fontSize: "clamp(3rem, 7vw, 7rem)",
          letterSpacing: "-0.04em"
        }}>
            <span className="block">We exist</span>
            <span className="block">for the</span>
            <span className="block relative inline-block">
              <span className="relative inline-block pb-2" style={{
              backgroundImage: "linear-gradient(var(--primary), var(--primary))",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "0 100%",
              backgroundSize: "100% 8px"
            }}>
                mark.
              </span>
            </span>
          </h2>

          {}
          <div className="font-default" style={{
          color: "var(--foreground)"
        }}>
            {paragraphs.map((paragraph, i) => <div data-index={i} key={i}>
                {i === 0 ? <p className="relative" style={{
              fontSize: "19px",
              lineHeight: 1.7
            }}>
                    <span className="float-left font-default font-bold mr-3 mt-1 leading-[0.85]" style={{
                fontSize: "96px",
                color: "var(--primary)"
              }}>
                      {paragraphs[0].charAt(0)}
                    </span>
                    {firstWord.split(/(compliance|excellence)/).map((chunk, idx) => {
                if (chunk === "compliance" || chunk === "excellence") {
                  return <span key={idx} className="pb-0.5" style={{
                    backgroundImage: "linear-gradient(var(--primary), var(--primary))",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "0 100%",
                    backgroundSize: "100% 2px"
                  }}>
                            {chunk}
                          </span>;
                }
                return <span key={idx}>{chunk}</span>;
              })}
                  </p> : <p className="mt-6" style={{
              fontSize: "19px",
              lineHeight: 1.7
            }}>
                    {i === 2 && paragraph.split(/(calibrate them micron by micron)/).map((chunk, idx) => chunk === "calibrate them micron by micron" ? <span key={idx} className="pb-0.5" style={{
                backgroundImage: "linear-gradient(var(--primary), var(--primary))",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "0 100%",
                backgroundSize: "100% 2px"
              }}>
                            {chunk}
                          </span> : <span key={idx}>{chunk}</span>)}
                    {i === 5 && paragraph.split(/(Efficiency is not strategy here)/).map((chunk, idx) => chunk === "Efficiency is not strategy here" ? <span key={idx} className="pb-0.5" style={{
                backgroundImage: "linear-gradient(var(--primary), var(--primary))",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "0 100%",
                backgroundSize: "100% 2px"
              }}>
                              {chunk}
                            </span> : <span key={idx}>{chunk}</span>)}
                    {i !== 2 && i !== 5 && paragraph}
                  </p>}

                {}
                {(i + 1) % 2 === 0 && i !== paragraphs.length - 1 && <div className="flex my-10">
                    <Separator className="!w-16 !h-px" style={{
                backgroundColor: "var(--primary)"
              }} />
                  </div>}
              </div>)}

            {}
            <div className="mt-16">
              <p className="font-default italic" style={{
              fontSize: "19px",
              lineHeight: 1.7
            }}>
                Engineering Team, Lead
              </p>
              <p className="font-mono text-xs mt-3" style={{
              color: "var(--muted-foreground)",
              letterSpacing: "0.15em"
            }}>EST. 2026</p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}